import { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import Api from "lib/api/httpRequestHelper";
import MaskAndValid from "utils/maskAndValid";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import InputText from "components/_refactor/text/input";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

const ESPECIE_OPTIONS = [
  { id: 0, label: "Caixa Papelão", value: "CAIXA PAPELAO" },
  { id: 1, label: "Fardos", value: "FARDOS" },
  { id: 2, label: "Avulso", value: "AVULSO" },
  { id: 3, label: "Pacotes", value: "PACOTES" },
  { id: 4, label: "Volumes", value: "VOLUMES" },
  { id: 5, label: "Rolos", value: "ROLOS" },
  { id: 6, label: "Pacote", value: "PACOTE" },
  { id: 7, label: "Envelope Pequeno", value: "ENVELOPE PEQUENO" },
  { id: 8, label: "Envelope Grande", value: "ENVELOPE GRANDE" },
  { id: 9, label: "Pallet", value: "PALLET" },
  { id: 10, label: "Fardo", value: "FARDO" },
];

function formatPeso(value) {
  if (value === "" || value == null) return "";
  const num = Number(String(value).replace(/\D/g, ""));
  return Number.isNaN(num) ? "" : num.toLocaleString("pt-BR");
}

function getRequiresWeightAndSpecies(itemsWithMotivos) {
  const motivo = (itemsWithMotivos || [])[0]?.motivo;
  return (
    motivo?.ObrigaPesoEspecie === true ||
    motivo?.RequiredWeightAndSpecies === 1 ||
    motivo?.RequiredWeightAndSpecies === true
  );
}

export default function SolicitarDevolucaoStep2({
  solicitacao,
  fieldsData,
  handleClear,
  setStep,
  setItem,
  returnToInitialPage,
  items = [],
  itemsWithMotivos = [],
  motivoGeral,
  defeitoGeral,
  plataform,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWeightError, setShowWeightError] = useState(false);
  const [formData, setFormData] = useState({
    especie: null,
    quantidadeVolumes: "",
    pesoBruto: "",
    email: "",
  });

  const requiresWeightAndSpecies = getRequiresWeightAndSpecies(itemsWithMotivos);

  const {
    IdSolicitacao: idSolicitacao,
    PesoLiquidoFormat: pesoLiquidoFormat,
    PesoLiquido: pesoLiquido,
    ExigirInformacaoVolume: exigirInformacaoVolume,
    ExigeVolume: exigeVolume,
    Volumes: volumesFromApi = [],
  } = solicitacao || {};

  const validaVolumes = exigirInformacaoVolume ?? exigeVolume ?? false;

  const [volumesList, setVolumesList] = useState(() => {
    const vols = volumesFromApi || [];
    const normalize = (s) => (s || "").toString().toUpperCase().replace(/\s/g, "");
    return vols.map((v, idx) => ({
      id: v.IdVolume ?? `vol-${Date.now()}-${idx}-${Math.random()}`,
      idProduto: v.IdProduto ?? null,
      especie: ESPECIE_OPTIONS.find((o) => o.value === normalize(v.Especie))
        || { id: 0, label: v.Especie || "", value: normalize(v.Especie) || "" },
      quantidadeVolumes: v.QuantidadeVolumes ?? "",
      pesoBruto: v.PesoBruto != null ? String(v.PesoBruto).replace(".", ",") : "",
    }));
  });

  const volumeFieldsEnabled = !isSubmitting;
  const canAddVolume =
    volumeFieldsEnabled &&
    !!formData.especie &&
    !!formData.quantidadeVolumes &&
    !!formData.pesoBruto;

  const snackbar = useSnackbar();

  const totalPesoBruto = volumesList.reduce((sum, v) => {
    const n = parseFloat(String(v.pesoBruto || 0).replace(",", "."));
    return (sum + (Number.isNaN(n) ? 0 : n)) * (v.quantidadeVolumes || 1);
  }, 0);
  const pesoLiquidoNum = parseFloat(pesoLiquido) || 0;

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "pesoBruto") {
      setShowWeightError(false);
    }
  };

  const handleEspecieChange = (value) => {
    setFormData((prev) => ({ ...prev, especie: value }));
  };

  const handleAddVolume = () => {
    if (!formData.especie || !formData.quantidadeVolumes || !formData.pesoBruto) return;
    const peso = parseFloat(String(formData.pesoBruto).replace(",", "."));
    if (Number.isNaN(peso)) return;

    setVolumesList((prev) => [
      ...prev,
      {
        id: `vol-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        especie: formData.especie,
        quantidadeVolumes: formData.quantidadeVolumes,
        pesoBruto: formData.pesoBruto,
      },
    ]);
    setFormData((prev) => ({
      ...prev,
      especie: null,
      quantidadeVolumes: "",
      pesoBruto: "",
    }));
    setShowWeightError(false);
  };

  const handleRemoveVolume = (id) => {
    setVolumesList((prev) => prev.filter((v) => v.id !== id));
    setShowWeightError(false);
  };

  const handleReturn = () => {
    setStep(1);
  };

  const getSubmitDisabled = () => {
    if (isSubmitting || !formData.email) return true;
    if (requiresWeightAndSpecies) {
      if (volumesList.length === 0) return true;
      if (totalPesoBruto < pesoLiquidoNum) return true;
    }
    if (validaVolumes) {
      if (volumesList.length === 0) return true;
      if (totalPesoBruto < pesoLiquidoNum) return true;
    }
    return false;
  };

  const handleSubmit = async () => {
    if (requiresWeightAndSpecies && volumesList.length === 0) {
      snackbar.showMessage(
        "Para o motivo selecionado é obrigatório informar ao menos um volume com peso e espécie.",
        { severity: "error" }
      );
      return;
    }
    if (validaVolumes && volumesList.length === 0) {
      snackbar.showMessage("Informe ao menos um volume.", { severity: "error" });
      return;
    }
    if ((requiresWeightAndSpecies || validaVolumes) && totalPesoBruto < pesoLiquidoNum) {
      setShowWeightError(true);
      snackbar.showMessage("O total do peso bruto deve ser maior ou igual ao peso líquido.", {
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const volumesPayload =
        volumesList.length > 0
          ? volumesList.map((v) => ({
              especie: v.especie?.value ?? "",
              quantidadeVolume: Number(v.quantidadeVolumes) || 0,
              pesoBruto: Number(String(v.pesoBruto).replace(",", ".")) || 0,
            }))
          : [];

      const body = {
        idSolicitacao,
        statusSolicitacao: "ABERTA",
        emailEnvio: formData.email,
      };

      if (volumesPayload.length > 0) {
        body.volumes = volumesPayload;
      }

      const res = await Api.put(
        "edit-request-reason",
        "trocasDevolucoes",
        body,
        true
      );

      if (res.Success) {
        setItem([]);
        snackbar.showMessage(res.Result.MessageResponse, {
          severity: "success",
        });
        setFormData({
          especie: null,
          pesoBruto: "",
          email: "",
          quantidadeVolumes: "",
        });
        setVolumesList([]);
        handleClear();
        returnToInitialPage();
      }
    } catch (error) {
      snackbar.showMessage(error, { severity: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Card>
        <CardContent>
          <Typography variant="h2">Informações da solicitação</Typography>

          <Grid container spacing={2} style={{ marginTop: "1rem" }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h4">Grupo</Typography>
              <Typography variant="caption">
                {fieldsData?.groupName?.Nome || ""}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4">Razão Social</Typography>
              <Typography variant="caption">
                {fieldsData?.socialName?.Nome
                  ? fieldsData.socialName.Nome.split("|")[0].trim()
                  : ""}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4">CNPJ</Typography>
              <Typography variant="caption">
                {fieldsData?.cnpj ? MaskAndValid.maskCNPJ(fieldsData.cnpj) : ""}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4">Motivo Devolução</Typography>
              <Typography variant="caption">
                {motivoGeral?.Motivo ?? "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4">Defeito Devolução</Typography>
              <Typography variant="caption">
                {defeitoGeral?.Defeito ?? "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <InputText
                label="E-mail de contato *"
                value={formData.email}
                onChange={handleInputChange("email")}
                disabled={isSubmitting}
                type="email"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h2">Informações de Volumes</Typography>

            <Grid container spacing={2} style={{ marginTop: "1rem" }}>
              <Grid item xs={12} md={4}>
                <InputText
                  label="Peso Líquido"
                  value={pesoLiquidoFormat}
                  disabled={true}
                  readOnly={true}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="center" style={{ marginTop: "0.5rem", paddingBottom: "1rem" }}>
              <Grid item xs={12} md={3}>
                <AutoCompleteField
                  label={requiresWeightAndSpecies ? "Especie *" : "Especie"}
                  onChange={handleEspecieChange}
                  value={formData.especie}
                  options={ESPECIE_OPTIONS}
                  disabled={!volumeFieldsEnabled}
                  optionLabelKey="label"
                  optionIdKey="id"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <InputText
                  label={validaVolumes || requiresWeightAndSpecies ? "Quantidade de Volumes *" : "Quantidade de Volumes"}
                  value={formData.quantidadeVolumes}
                  onChange={handleInputChange("quantidadeVolumes")}
                  disabled={!volumeFieldsEnabled}
                  type="number"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <InputText
                  label={requiresWeightAndSpecies ? "Peso Bruto *" : "Peso Bruto"}
                  value={formData.pesoBruto}
                  onChange={handleInputChange("pesoBruto")}
                  disabled={!volumeFieldsEnabled}
                  type="number"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddVolume}
                  disabled={!canAddVolume}
                  startIcon={<AddIcon />}
                  fullWidth
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "1.3rem", height: "40px" }}
                >
                  Adicionar
                </Button>
              </Grid>
            </Grid>

            {volumesList.length > 0 && (
              <>
                <Table size="small" style={{ marginTop: "1rem" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Espécie</TableCell>
                      <TableCell align="right">Quantidade de Volumes</TableCell>
                      <TableCell align="right">Peso Bruto</TableCell>
                      <TableCell align="center" padding="none" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {volumesList.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.especie?.label ?? row.especie?.value ?? ""}</TableCell>
                        <TableCell align="right">{row.quantidadeVolumes}</TableCell>
                        <TableCell align="right">{formatPeso(row.pesoBruto)}</TableCell>
                        <TableCell align="center" padding="none">
                          <IconButton
                            aria-label="Excluir"
                            size="small"
                            onClick={() => handleRemoveVolume(row.id)}
                            disabled={isSubmitting}
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Typography variant="body1" style={{ marginTop: "0.5rem" }}>
                  Total Peso bruto: {formatPeso(totalPesoBruto)}
                </Typography>
              </>
            )}

            {showWeightError && (
              <Typography
                variant="h5"
                component="h5"
                style={{ color: "grey", paddingLeft: "12px", marginTop: "0.5rem" }}
              >
                <i
                  className="fa fa-exclamation-circle"
                  aria-hidden="true"
                  style={{ marginRight: "8px" }}
                />{" "}
                O total do peso bruto deve ser maior ou igual ao peso líquido
              </Typography>
            )}
          </CardContent>
        </Card>

      <Grid
        container
        spacing={2}
        style={{ justifyContent: "flex-end", margin: "1rem 0" }}
      >
        <Grid item xs={6} md={3}>
          <Button
            variant="outlined"
            onClick={handleReturn}
            disabled={isSubmitting}
          >
            Voltar
          </Button>
        </Grid>
        <Grid item xs={6} md={3}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={getSubmitDisabled()}
          >
            Avançar
          </Button>
        </Grid>
      </Grid>
    </div>
  );
}
